(function(){
  const RECENT_KEY = "lioi-smart-search-recent";
  const analyticsTimers = new WeakMap();
  const analyticsLast = new WeakMap();
  const normalize = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const splitTerms = (value) => normalize(value).split(/\s+/).filter(Boolean);
  const escapeHtml = (value) => String(value == null ? "" : value).replace(/[&<>"']/g, (char) => ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#039;" }[char]));
  const absoluteUrl = (url) => {
    const raw = String(url || "").trim();
    if (!raw) return "#";
    if (/^https?:\/\//i.test(raw)) return raw;
    return raw.startsWith("/") ? raw : "/" + raw;
  };

  function getRecentSearches() {
    try {
      const items = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
      return Array.isArray(items) ? items.filter(Boolean).slice(0, 7) : [];
    } catch {
      return [];
    }
  }

  function saveRecentSearch(query) {
    const clean = String(query || "").trim();
    if (!clean) return;
    const items = [clean].concat(getRecentSearches().filter((item) => normalize(item) !== normalize(clean))).slice(0, 7);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(items));
    } catch {}
  }

  function sendAnalytics(root, payload) {
    const url = String(root.dataset.analyticsUrl || "").trim();
    if (!url || !payload?.query) return;
    try {
      const body = JSON.stringify(Object.assign({ source: "lioi-smart-search" }, payload));
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
        return;
      }
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true
      }).catch(() => {});
    } catch {}
  }

  function scheduleSearchAnalytics(root, payload) {
    const key = normalize(payload?.query || "") + "|" + String(payload?.results || 0);
    if (!key.trim() || analyticsLast.get(root) === key) return;
    clearTimeout(analyticsTimers.get(root));
    analyticsTimers.set(root, setTimeout(() => {
      analyticsLast.set(root, key);
      sendAnalytics(root, payload);
    }, 850));
  }

  function expandTerms(tokens, rules) {
    const expanded = new Set(tokens);
    (rules.synonyms || []).forEach((group) => {
      const terms = [group.term].concat(group.aliases || []).map(normalize).filter(Boolean);
      if (terms.some((term) => tokens.includes(term))) {
        terms.forEach((term) => expanded.add(term));
      }
    });
    return Array.from(expanded);
  }

  function scoreItem(item, query, tokens, rules) {
    if (!tokens.length) return 0;
    const title = normalize(item.title);
    const terms = normalize(item.terms);
    const skuText = normalize((item.skus || []).join(" "));
    let score = 0;

    tokens.forEach((token) => {
      if (!token) return;
      if (title === token) score += 90;
      else if (title.startsWith(token)) score += 55;
      else if (title.includes(token)) score += 32;
      if (skuText.split(" ").includes(token)) score += 70;
      else if (skuText.includes(token)) score += 42;
      if (terms.includes(token)) score += 18;
    });

    (rules.boosts || []).forEach((boost) => {
      const boostQuery = normalize(boost.query);
      if (!boostQuery || !query.includes(boostQuery)) return;
      const haystack = normalize([item.id, item.productId, item.title, item.subtitle, item.url].concat(item.skus || []).join(" "));
      if ((boost.matches || []).some((match) => haystack.includes(normalize(match)))) score += 120;
    });

    if (item.type === "product") score += 6;
    if (item.type === "catalog") score += 4;
    return score;
  }

  function findRedirect(query, rules) {
    const normalized = normalize(query);
    return (rules.redirects || []).find((redirect) => normalize(redirect.query) === normalized);
  }

  function findCollectionRule(query, rules) {
    const normalized = normalize(query);
    return (rules.collectionRules || []).find((rule) => normalize(rule.query) === normalized);
  }

  function getRecommendations(index, limit) {
    const ids = new Set(index.recommendations || []);
    const selected = (index.items || []).filter((item) => ids.has(item.id));
    if (selected.length) return selected.slice(0, limit);
    return (index.items || []).filter((item) => item.type === "product").slice(0, limit);
  }

  function getSearchMatches(index, normalizedQuery, tokens) {
    const collectionRule = findCollectionRule(normalizedQuery, index.rules || {});
    const collection = normalize(collectionRule && collectionRule.collection);

    return (index.items || [])
      .map((item) => {
        let score = scoreItem(item, normalizedQuery, tokens, index.rules || {});
        if (collection && item.type === "product" && normalize(item.subtitle) === collection) score += 46;
        return { item, score };
      })
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score);
  }

  function getFullSearchUrl(query) {
    return "/search?type=product&q=" + encodeURIComponent(String(query || "").trim());
  }

  function goToFullSearch(root, query, resultCount) {
    const clean = String(query || "").trim();
    if (!clean) return;
    saveRecentSearch(clean);
    sendAnalytics(root, { type: "submit", query: clean, results: resultCount || 0 });
    window.location.href = getFullSearchUrl(clean);
  }

  function renderSearchCard(item, query) {
    const image = item.image ? '<img src="' + escapeHtml(item.image) + '" alt="" loading="lazy">' : '<span class="lioi-search-fallback">' + escapeHtml((item.type || "?").slice(0, 1).toUpperCase()) + '</span>';
    const meta = [item.subtitle, item.skus && item.skus.length ? item.skus.slice(0, 3).join(" / ") : ""].filter(Boolean).join(" - ");
    const typeLabel = item.type === "catalog" ? "Catalogo" : item.type === "page" ? "Pagina" : "Producto";
    return '<a class="lioi-search-result" data-lioi-search-hit data-query="' + escapeHtml(query || item.title || "") + '" href="' + escapeHtml(absoluteUrl(item.url)) + '">' + image + '<span><em>' + escapeHtml(typeLabel) + '</em><strong>' + escapeHtml(item.title) + '</strong><small>' + escapeHtml(meta) + '</small></span></a>';
  }

  function renderRecentSearches(root, index) {
    const recent = getRecentSearches();
    if (!recent.length) {
      return '<div class="lioi-search-empty compact">Todavia no hay busquedas recientes.</div>';
    }
    return recent.map((query) => '<button type="button" class="lioi-search-recent" data-lioi-recent-search="' + escapeHtml(query) + '">' + escapeHtml(query) + '</button>').join("");
  }

  function bindPanelActions(root, index) {
    root.querySelectorAll("[data-lioi-recent-search]").forEach((button) => {
      button.addEventListener("click", () => {
        const input = root.querySelector("[data-lioi-search-input]");
        const value = button.getAttribute("data-lioi-recent-search") || "";
        if (!input) return;
        input.value = value;
        renderResults(root, index, value);
        input.focus();
      });
    });

    root.querySelectorAll("[data-lioi-search-hit]").forEach((link) => {
      link.addEventListener("click", () => {
        const query = link.getAttribute("data-query") || "";
        saveRecentSearch(query);
        sendAnalytics(root, {
          type: "click",
          query,
          targetTitle: link.querySelector("strong")?.textContent || "",
          targetUrl: link.getAttribute("href") || ""
        });
      });
    });

    root.querySelectorAll("[data-lioi-search-all]").forEach((link) => {
      link.addEventListener("click", () => {
        const query = link.getAttribute("data-query") || "";
        saveRecentSearch(query);
        sendAnalytics(root, {
          type: "view_all",
          query,
          targetTitle: "Ver todos los resultados",
          targetUrl: link.getAttribute("href") || ""
        });
      });
    });
  }

  function renderResults(root, index, query) {
    const resultsNode = root.querySelector("[data-lioi-search-results]");
    if (!resultsNode) return;
    const normalizedQuery = normalize(query);
    const limit = Math.max(3, Math.min(Number(root.dataset.limit || 8), 12));
    root.classList.add("is-open");

    if (!normalizedQuery) {
      const recommended = getRecommendations(index, limit);
      resultsNode.innerHTML = '<div class="lioi-search-panel">' +
        '<aside class="lioi-search-side"><span>Ultimos buscados</span>' + renderRecentSearches(root, index) + '</aside>' +
        '<section class="lioi-search-main"><div class="lioi-search-panel-head"><span>Recomendados</span><small>Seleccionados desde el panel Lioi</small></div>' +
        '<div class="lioi-search-recommend-grid">' + recommended.map((item) => renderSearchCard(item, item.title)).join("") + '</div></section>' +
        '</div>';
      bindPanelActions(root, index);
      return;
    }

    const baseTokens = splitTerms(query);
    const tokens = expandTerms(baseTokens, index.rules || {});
    const redirect = findRedirect(query, index.rules || {});
    const allMatches = getSearchMatches(index, normalizedQuery, tokens);
    const matches = allMatches.slice(0, limit);
    const totalResults = allMatches.length + (redirect ? 1 : 0);
    scheduleSearchAnalytics(root, { type: "search", query, results: totalResults });

    const redirectHtml = redirect ? '<a class="lioi-search-result lioi-search-redirect" href="' + escapeHtml(absoluteUrl(redirect.url)) + '"><span>Ir directo</span><strong>' + escapeHtml(redirect.query) + '</strong></a>' : "";
    const matchesHtml = matches.map(({ item }) => renderSearchCard(item, query)).join("");
    const fullSearchHtml = totalResults > matches.length + (redirect ? 1 : 0)
      ? '<a class="lioi-search-view-all" href="' + escapeHtml(getFullSearchUrl(query)) + '" data-lioi-search-all data-query="' + escapeHtml(query) + '">Ver todos los resultados</a>'
      : "";

    resultsNode.innerHTML = '<div class="lioi-search-panel">' +
      '<aside class="lioi-search-side"><span>Ultimos buscados</span>' + renderRecentSearches(root, index) + '</aside>' +
      '<section class="lioi-search-main"><div class="lioi-search-panel-head"><span>Resultados</span><small>' + escapeHtml(totalResults) + ' coincidencias</small></div>' +
      '<div class="lioi-search-results-list">' + (redirectHtml + matchesHtml + fullSearchHtml || '<div class="lioi-search-empty">No encontramos resultados. Prueba con otro nombre o SKU.</div>') + '</div></section>' +
      '</div>';
    bindPanelActions(root, index);
  }

  async function init(root) {
    const input = root.querySelector("[data-lioi-search-input]");
    const resultsNode = root.querySelector("[data-lioi-search-results]");
    if (!input || !resultsNode) return;
    try {
      const response = await fetch(root.dataset.indexUrl, { cache: "no-store" });
      if (!response.ok) throw new Error("HTTP " + response.status);
      const index = await response.json();
      root.dataset.ready = "true";
      input.addEventListener("input", () => renderResults(root, index, input.value));
      input.addEventListener("focus", () => renderResults(root, index, input.value));
      input.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          const query = input.value;
          const normalizedQuery = normalize(query);
          const tokens = expandTerms(splitTerms(query), index.rules || {});
          const resultCount = normalizedQuery ? getSearchMatches(index, normalizedQuery, tokens).filter((entry) => entry.item.type === "product").length : 0;
          goToFullSearch(root, query, resultCount);
        }
        if (event.key === "Escape") root.classList.remove("is-open");
      });
      document.addEventListener("click", (event) => {
        if (!root.contains(event.target)) root.classList.remove("is-open");
      });
    } catch (error) {
      console.error("Lioi Smart Search:", error);
      resultsNode.innerHTML = '<div class="lioi-search-empty">Buscador no disponible.</div>';
    }
  }

  document.addEventListener("DOMContentLoaded", function(){
    document.querySelectorAll("[data-lioi-smart-search]").forEach(init);
  });
})();